import { BUTTON_SIZE, BUTTON_TYPE, DSButton, TAB_TYPE, Tab, useToast } from '@repo/design-system/index'
import { QIcon_bookmark, QIcon_chevron_bold_left, QIcon_share } from '@repo/icons/index'
import { QueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import groq from 'groq'
import { GetServerSideProps } from 'next'
import cookies from 'next-cookies'
import { useRouter } from 'next/router'
import { createContext, useEffect, useState } from 'react'
import PortalContainer from '../../../../../components/container/PortalContainer'
import { TAB } from '../../../../../components/materials/const'
import MaterialBodyComponent from '../../../../../components/new-portal/materials/MaterialBodyComponent'
import MaterialDesktopBottomTab from '../../../../../components/new-portal/materials/MaterialDesktopBottomTab'
import MaterialMobileBottomTab from '../../../../../components/new-portal/materials/MaterialMobileBottomTab'
import SharingTooltip, { SHARING_TOOLTIP_LOCATION } from '../../../../../components/new-portal/materials/SharingTooltip'
import apis from '../../../../../modules/apis'
import { axiosGet, axiosPost } from '../../../../../modules/axiosHelper'
import { useCurrentUser } from '../../../../../modules/context/UserContext'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../../../modules/hooks/common/useIsWebview'
import { useOnclickHeartPacket } from '../../../../../modules/hooks/common/useOnclickHeartPacket'
import { LOCALE_KO } from '../../../../../modules/i18n/config'
import { n_common, n_material, s_materials, s_preparation } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import client from '../../../../../modules/sanityClient'
import { track } from '../../../../../modules/track'
import urls from '../../../../../modules/urls'
import { getOgUrl } from '../../../../../modules/utils'
import { COLORS, COOKIE_LIST, COOKIE_OPT, LOCATION, LOG_CATEGORY, LOG_TYPE } from '../../../../../modules/vars'
import { MATERIAL_DETAIL_GET_DATA_QUERYResult } from '../../../../../sanity/types'

const SharingBtn = ({ materialId, course, sharingTooltip, setSharingTooltip }) => {
  const { isLocaleKO } = useTranslation()

  return (
    <div className="relative">
      <div
        onClick={() => setSharingTooltip((prev) => !prev)}
        className="relative z-40 flex h-[38px] select-none items-center justify-center rounded-full px-[10px]"
      >
        <SharingTooltip
          xs
          outlined
          bottom={-132}
          component={
            <div className="cursor-pointer">
              <QIcon_share />
            </div>
          }
          tooltipLocation={SHARING_TOOLTIP_LOCATION.PORTAL_MATERIAL}
          webUrl={urls.student.landing.contents.material.detail(materialId)}
          title={isLocaleKO ? course.title_kr : course.title_en}
          subtitle={isLocaleKO ? course.subtitle_kr : course.subtitle_en}
          imageUrl={course.image_long_url}
          contentsId={materialId}
          showTooltip={sharingTooltip}
          setShowToolTip={setSharingTooltip}
        />
      </div>
    </div>
  )
}

export const MaterialTab = ({ course }) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { query, back } = useRouter()
  const isLessonSelect = Boolean(query.lessonId)
  const { toastInfo, toastError } = useToast()
  const isWebview = useIsWebview()

  const [sharingTooltip, setSharingTooltip] = useState(false)
  const [isMine, setIsMine] = useState(course.is_mine)
  const [loading, setLoading] = useState(false)
  const onClickHeartPacket = useOnclickHeartPacket({
    isList: false,
    onAdded: () => {
      setIsMine(true)
      toastInfo(t(n_material.toast_book_mark))
    },
    onDeleted: () => {
      setIsMine(false)
      toastInfo(t(n_material.toast_delete_book_mark))
    },
  })

  const handleSelectMaterial = async (lessonId, materalId) => {
    try {
      if (!lessonId) {
        toastError('Error : No classes have been assigned.')
        return
      }

      setLoading(true)
      const response = await axiosPost(apis.lessons.editCourse(), {
        lesson_id: lessonId,
        course_id: materalId,
      })

      if (response.success) {
        // [Plus] 교재 변경 성공시 페이지 이동해서 toast 띄우는 로직
        router.push(urls.student.newPortal.lessons.upcomingLessons + '?update=material')
      }
    } catch (e) {
      setLoading(false)
      alert(e.message)
    }
  }

  return (
    <div className={classNames(isWebview && 'hidden', 'border-b-1 ml-[0px] flex h-[60px] w-full bg-white px-[16px]')}>
      <div className="mx-auto flex w-full max-w-[1040px] items-center justify-between">
        <div className="Body2-14Bd flex cursor-pointer flex-row items-center lg:pl-[16px]" onClick={() => back()}>
          <QIcon_chevron_bold_left width={16} height={16} />
          <div className="pt-[0px]">{t(n_common.btn_back_list)}</div>
        </div>
        <div className="flex flex-row items-center gap-[20px]">
          <button className="" onClick={(e) => onClickHeartPacket(e, course)}>
            <QIcon_bookmark color={isMine ? COLORS.RED400 : COLORS.GRAY300} />
          </button>
          <div className="">
            {isLessonSelect ? (
              <div className="">
                <DSButton
                  isLoading={loading}
                  size={BUTTON_SIZE.MD}
                  type={BUTTON_TYPE.SOLID}
                  onClick={() => handleSelectMaterial(query.lessonId, query.materialId)}
                >
                  <DSButton.Label>{t(n_material.select)}</DSButton.Label>
                </DSButton>
              </div>
            ) : (
              <SharingBtn
                course={course}
                materialId={query.materialId}
                sharingTooltip={sharingTooltip}
                setSharingTooltip={setSharingTooltip}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
type MaterialDetailPropsType = {
  materialData: any
  ogData: any
}

export const MaterialSubTab = ({ activeTabIdx, setActiveTabIdx }) => {
  const { t } = useTranslation()

  const SUB_MENU_LIST = [
    {
      label: t(s_preparation.insight),
    },
    {
      label: t(s_materials.key_questions),
    },
    {
      label: t(s_materials.clip_article),
    },
  ]

  return (
    <div className="border-b-1 z-[20] ml-[0px] w-screen bg-white sm:w-full">
      <div className="mx-auto max-w-[1040px]">
        <Tab
          className="h-[46px] overflow-hidden"
          type={TAB_TYPE.SCROLL}
          borderNone={true}
          activeTabIdx={activeTabIdx}
          setActiveTabIdx={setActiveTabIdx}
        >
          {SUB_MENU_LIST.map((subMenuItem, i) => {
            return <Tab.TabItem name={subMenuItem.label} key={i} />
          })}
        </Tab>
      </div>
    </div>
  )
}

export const TEXT_SIZE = {
  DEFAULT: 'default',
  BIGGER: 'bigger',
  SMALLER: 'smaller',
}

export const TextContext = createContext({
  showKorean: false,
  setShowKorean: null,
  textSize: TEXT_SIZE.DEFAULT,
  setTextSize: null,
})

// 한글 같이 보기 버튼에 대한 페이지 전역 처리
export function TextProvider({ children }) {
  const [showKorean, setShowKorean] = useState<boolean>(false)
  const [textSize, setTextSize] = useState<string>(TEXT_SIZE.DEFAULT)
  return (
    <TextContext.Provider value={{ showKorean, setShowKorean, textSize, setTextSize }}>{children}</TextContext.Provider>
  )
}

const MaterialDetail = ({ materialData, ogData }: MaterialDetailPropsType) => {
  const { currentUser } = useCurrentUser()
  const { query } = useRouter()
  const [activeTabIdx, setActiveTabIdx] = useState<number>(0)
  const isLessonSelect = Boolean(query.lessonId)
  // 탭 클릭시 트래킹
  useEffect(() => {
    if (materialData?.free_talking) return
    let duration = 1
    let log_id = null
    const interval = setInterval(() => {
      duration += 1
      if (duration >= 5) {
        switch (activeTabIdx) {
          case TAB.INSIGHT:
            track({
              location: LOCATION.COURSE,
              log_type: LOG_TYPE.COURSE.READ,
              log_category: LOG_CATEGORY.READ,
              course_id: materialData.id,
              duration: duration,
              log_id: log_id,
            }).then((res) => {
              if (!log_id) {
                log_id = res?.log?.id
              }
            })
            break
          case TAB.EXPRESSIONS:
            track({
              location: LOCATION.COURSE,
              log_type: LOG_TYPE.COURSE.EXPRESSION_STUDY,
              log_category: LOG_CATEGORY.READ,
              log_id: log_id,
              course_id: materialData.id,
              duration: duration,
            }).then((res) => {
              if (!log_id) {
                log_id = res?.log?.id
              }
            })
            break
          default:
            break
        }
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [activeTabIdx])

  if (!currentUser) {
    return <></>
  }

  return (
    <TextProvider>
      <div className="relative h-screen">
        <PortalContainer
          showBookButton={!isLessonSelect}
          ogData={ogData}
          noDefaultBar={true}
          topBar={<MaterialTab course={materialData?.course} />}
          subTopBar={<MaterialSubTab activeTabIdx={activeTabIdx} setActiveTabIdx={setActiveTabIdx} />}
        >
          <div className="relative mt-[80px] h-screen lg:mx-[16px]">
            <MaterialBodyComponent
              activeTabIdx={activeTabIdx}
              materialData={materialData}
              materialId={query.materialId}
            />
          </div>
        </PortalContainer>
        {activeTabIdx === 0 && (
          <>
            <div className="block sm:hidden">
              <MaterialMobileBottomTab course={materialData?.course} />
            </div>
            <div className=" z-[10] hidden flex-row sm:flex">
              <MaterialDesktopBottomTab course={materialData?.course} />
            </div>
          </>
        )}
      </div>
    </TextProvider>
  )
}

export const fetchMaterial = async (materialId: string): Promise<MATERIAL_DETAIL_GET_DATA_QUERYResult> => {
  const params = {
    materialId: materialId,
  }

  const post = await client.fetch(groq`
  *[_type == "contentInsight" && slug.current == "${materialId}"][0]`)

  return post
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { materialId, lang = LOCALE_KO } = context.query
  const { jwt_token } = cookies(context)

  if (!jwt_token) {
    redirectToHome(lang, context)
    return
  }

  try {
    const queryClient = new QueryClient()

    const [_, json] = await Promise.all([
      queryClient.prefetchQuery(['material', materialId], () => fetchMaterial(materialId as string)),
      axiosGet(apis.course.detail({ id: materialId }), { initialToken: jwt_token }),
    ])

    if (!json.success) {
      redirectToHome(lang, context)
      return
    }

    const materialOgData = {
      title: lang === LOCALE_KO ? json.course.title_kr : json.course.title_en,
      desc: lang === LOCALE_KO ? json.course.subtitle_kr : json.course.subtitle_en,
      image: json.course.image_url,
      ogUrl: getOgUrl({ lang, context: { pathname: context.resolvedUrl } }),
    }

    return {
      props: {
        materialData: json,
        ogData: materialOgData,
      },
    }
  } catch (error) {
    // Handle error appropriately
    console.error('Error fetching course details:', error)
    redirectToHome(lang, context)
  }
}

function redirectToHome(lang, context) {
  context.res.writeHead(302, {
    Location: `/${lang}/`,
    // Add Set-Cookie header only if jwt_token is missing
    ...(context.jwt_token
      ? {}
      : {
          'Set-Cookie': `${COOKIE_LIST.LOGIN_AFTER_URL}=${context.resolvedUrl.replace('/[lang]/', '/')}; Domain=${
            COOKIE_OPT.domain
          }; Path=${COOKIE_OPT.path}`,
        }),
  })
  context.res.end()
}

export default MaterialDetail
